import {
  generateAndSendNewPin,
  getPhoneNumberAvailability,
  setCustomerSession,
  signInCustomer
} from "api";
import {
  IInterest,
  ILoggedCustomer,
  ITabletData,
  ITabletLocation,
  IUsageType,
  PhoneNumberAvailability,
  TabletOperationMode
} from "api/types";
import { ApolloClient } from "apollo-boost";
import { useAppState } from "AppState";
import ManageButton from "components/common/ManageButton/ManageButton";
import NumPad, { MediumCenteredNumPad } from "components/common/NumPad";
import RightLeftLayout from "components/common/RightLeftLayout/RightLeftLayout";
import { TabletDataContext } from "components/Gates/TabletDataGate";

import * as React from "react";
import { withApollo } from "react-apollo";
import { Routes } from "router";
import OnboardingContainer from "screens/Onboarding/Onboarding.container";
import DispensaryContainer from "screens/Onboarding/WelcomeBoard/DispensaryContainer/DispensaryContainer";
import { getCurrentLocation } from "utils/apis";
import { getVendorImageUrl } from "utils/ConfigLoader";

interface IProps {
  history?: any;
  client?: ApolloClient<any>;
  canSignIn?: boolean;
  canSignUp?: boolean;
  tabletData?: ITabletData;
  handleCustomerSession?(loggedCustomer: ILoggedCustomer): void;
  onSignUp?(): void;
}

interface IState {
  step: number;
  currentControl: string;
  signIn?: boolean;
  signUp?: boolean;
  signInWithTcpa?: boolean;
  phone?: string;
  checkingPhone?: boolean;
  pin?: string;
  checkingPin?: boolean;
  goodSignInPin?: boolean;
  wrongSignInPin?: boolean;
  pinReset?: boolean;
  message?: string;
  interests?: IInterest[];
  usageTypes?: IInterest[];
  location?: ITabletLocation;
  vendorImageUrl?: string | null;
  pinGen?: string;
  phoneNumberAvailability?: PhoneNumberAvailability;
}

interface IAppStateProps {
  availableInterests?: IInterest[];
  availableUsageTypes?: IUsageType[];
  selectedInterestIds?: string[];
  selectedUsageTypeIds?: string[];
  setAppStateLocationName?(locationName: string): void;
  setSelectedInterestIds?(selectedInterestIds: string[]): void;
}

interface IOnboardingContainerProps {
  setPin: React.Dispatch<React.SetStateAction<string>>;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setInterestsInOnboarding: React.Dispatch<React.SetStateAction<IInterest[]>>;
  setUsageTypesInOnboarding: React.Dispatch<React.SetStateAction<IInterest[]>>;
}

const WelcomeBoardWrapper = ({ ...props }: IProps): JSX.Element => {
  const {
    setLocationName: setAppStateLocationName,
    availableInterests,
    availableUsageTypes,
    selectedInterestIds,
    selectedUsageTypeIds,
    setSelectedInterestIds
  } = useAppState();

  const {
    setPin,
    setPhoneNumber,
    setInterests: setInterestsInOnboarding,
    setUsageTypes: setUsageTypesInOnboarding
  } = OnboardingContainer.useContainer();

  const WelcomeBoardProps = {
    setAppStateLocationName,
    availableInterests,
    availableUsageTypes,
    selectedInterestIds,
    selectedUsageTypeIds,
    setSelectedInterestIds,
    setInterestsInOnboarding,
    setUsageTypesInOnboarding,
    setPin,
    setPhoneNumber,
    ...props
  };

  return <WelcomeBoard {...WelcomeBoardProps} />;
};

class WelcomeBoard extends React.Component<
  IProps & IAppStateProps & IOnboardingContainerProps,
  IState
> {
  public constructor(
    props: IProps & IAppStateProps & IOnboardingContainerProps
  ) {
    super(props);
    this.state = { step: 0, currentControl: "phone" };
  }

  public async componentDidMount() {
    const location = await getCurrentLocation(this.props.client!);
    const vendorImageUrl = location.vendorId
      ? getVendorImageUrl(location.vendorId)
      : null;
    this.setState({
      location,
      vendorImageUrl
    });
    if (this.props.setAppStateLocationName) {
      this.props.setAppStateLocationName(location.name);
    }
  }

  public render() {
    const {
      step,
      signIn,
      signUp,
      signInWithTcpa,
      phone = "",
      checkingPhone,
      currentControl,
      pin = "",
      checkingPin,
      goodSignInPin,
      wrongSignInPin,
      pinReset,
      message,
      location
    } = this.state;
    // TODO: Incorporate this masking inside component
    const pinGen = "﹡﹡﹡﹡".slice(0, (pin || "").length);
    const goToManage = () =>
      this.props.history.push(Routes.AuthorizationManage);

    return (
      <>
        {step === 0 && (
          <RightLeftLayout
            title={location ? "Welcome to " + location.name + "!" : ""}
            rightTitle="ENTER PHONE NUMBER"
            leftPanel={
              <DispensaryContainer
                step={step}
                currentControl={currentControl}
                signIn={signIn}
                signUp={signUp}
                phone={phone}
                checkingPhone={checkingPhone}
                checkingPin={checkingPin}
                goodSignInPin={goodSignInPin}
                wrongSignInPin={wrongSignInPin}
                pinReset={pinReset}
                message={message}
                location={location}
                pinGen={pinGen}
                onFocus={this.onFocus}
                sendNewPin={this.sendNewPin}
                nextStep={this.persistValuesAndGoToTcpa(pin, phone)}
              />
            }
            rightPanel={
              <MediumCenteredNumPad>
                <NumPad onTap={this.onTap} />
                <ManageButton onLongPress={goToManage} />
              </MediumCenteredNumPad>
            }
          />
        )}
      </>
    );
  }

  private persistValuesAndGoToTcpa = (pin, phone) => {
    return () => {
      const { history } = this.props;
      this.props.setPin(pin);
      this.props.setPhoneNumber(`+1${phone}`);
      history.push(Routes.SignUpTcpa);
    };
  };

  private onFocus = (control: string) => () => {
    this.setState({ currentControl: control });
  };

  private onTap = (value: string) => {
    let callback: () => void | undefined;
    this.setState(
      state => {
        switch (state.currentControl) {
          case "phone":
            const currentPhone = state.phone || "";
            let newPhone = currentPhone;
            if (value === "back") {
              newPhone = currentPhone.slice(0, currentPhone.length - 1);
            } else if (currentPhone.length < 10) {
              newPhone = `${currentPhone}${value}`;
            }

            if (newPhone !== currentPhone) {
              callback = () => {
                this.handlePhoneNumber();
              };
              return {
                phone: newPhone,
                signUp: false,
                signIn: false,
                pinReset: false,
                checkingPhone: newPhone.length >= 10,
                message: ""
              };
            }
            break;
          case "pin":
            const currentPin = state.pin || "";
            let newPin = currentPin;
            if (value === "back") {
              newPin = currentPin.slice(0, currentPin.length - 1);
            } else if (currentPin.length < 4) {
              newPin = `${currentPin}${value}`;
            }

            if (newPin !== currentPin) {
              callback = () => this.handlePin();
              return {
                pin: newPin,
                goodSignInPin: false,
                wrongSignInPin: false,
                checkingPin: state.signIn && newPin.length >= 4,
                message: ""
              };
            }
            break;
        }

        return {};
      },
      () => {
        if (callback) {
          callback();
        }
      }
    );
  };

  private handlePhoneNumber = async () => {
    const { canSignIn = true, canSignUp = true } = this.props;
    const { phone } = this.state;
    if (!phone || phone.length < 10) {
      return;
    }
    try {
      const phoneNumberAvailability: any = await getPhoneNumberAvailability(
        this.props.client!,
        phone ? `+1${phone}` : ""
      );

      if (
        phoneNumberAvailability === PhoneNumberAvailability.AvailableGlobally
      ) {
        if (canSignUp) {
          this.setState({
            signUp: true,
            signIn: false,
            currentControl: "pin",
            checkingPhone: false,
            message: ""
          });
        } else {
          this.setState({
            signUp: false,
            signIn: false,
            currentControl: "phone",
            checkingPhone: false,
            message: "This phone number is not registered"
          });
        }
      } else {
        if (
          phoneNumberAvailability ===
          PhoneNumberAvailability.AvailableAtThisLocation
        ) {
          this.setState(
            () => ({
              signUp: false,
              signIn: false,
              signInWithTcpa: true,
              currentControl: "pin",
              checkingPhone: false,
              message: ""
            }),
            () => this.handlePreSignIn()
          );
        } else if (canSignIn) {
          this.setState(
            () => ({
              signUp: false,
              signIn: true,
              currentControl: "pin",
              checkingPhone: false
            }),
            () => this.signIn()
          );
        } else {
          this.setState({
            signUp: false,
            signIn: false,
            currentControl: "phone",
            checkingPhone: false,
            message: "This phone number is already registered"
          });
        }
      }
    } catch (err) {
      console.error("error while checking phone number availability", err);
    }
  };

  private handlePin = () => {
    if (!this.state.pin || this.state.pin.length < 4) {
      return;
    }
    if (this.state.signInWithTcpa) {
      this.handlePreSignIn();
    } else if (this.state.signIn) {
      this.signIn();
    }
  };

  private handlePreSignIn = async () => {
    const { phone, pin } = this.state;
    try {
      const loggedCustomer = await signInCustomer(
        this.props.client!,
        phone ? `+1${this.state.phone}` : "",
        pin || "",
        this.props.tabletData
          ? this.props.tabletData.operationMode
          : TabletOperationMode.PointOfSale,
        true
      );
      if (!loggedCustomer) {
        this.setState({
          checkingPhone: false,
          checkingPin: false,
          currentControl: "pin",
          signUp: false,
          signIn: true,
          goodSignInPin: false,
          wrongSignInPin: (pin || "").length >= 4,
          message: ""
        });
      } else {
        this.props.setPhoneNumber(`+1${this.state.phone}`);
        this.props.history.push(Routes.NewAtLocationTcpaAcknowledgement);
      }
    } catch (err) {
      this.setState({
        checkingPhone: false,
        checkingPin: false,
        signUp: false,
        signIn: true,
        goodSignInPin: false,
        wrongSignInPin: (pin || "").length >= 4,
        message: ""
      });
    }
  };

  private signIn = async (isMockSignIn: boolean = false) => {
    const { phone, pin } = this.state;
    try {
      const loggedCustomer = await signInCustomer(
        this.props.client!,
        phone ? `+1${this.state.phone}` : "",
        pin || "",
        this.props.tabletData
          ? this.props.tabletData.operationMode
          : TabletOperationMode.PointOfSale,
        isMockSignIn
      );

      if (!loggedCustomer) {
        this.setState({
          checkingPhone: false,
          checkingPin: false,
          currentControl: "pin",
          signUp: false,
          signIn: true,
          goodSignInPin: false,
          wrongSignInPin: (pin || "").length >= 4,
          message: ""
        });
      } else {
        if (this.props.handleCustomerSession) {
          this.props.handleCustomerSession(loggedCustomer);
        } else {
          setCustomerSession(this.props.client!, loggedCustomer);
        }
      }
    } catch (err) {
      this.setState({
        checkingPhone: false,
        checkingPin: false,
        signUp: false,
        signIn: true,
        goodSignInPin: false,
        wrongSignInPin: (pin || "").length >= 4,
        message: ""
      });
    }
  };

  private sendNewPin = async () => {
    if (!this.state.phone) {
      return;
    }

    try {
      await generateAndSendNewPin(this.props.client!, `+1${this.state.phone}`);
    } catch {
      console.error("Error whiling generating and sending new Pin");
    }
    this.setState({ pinReset: true, message: "" });
  };
}

export default withApollo<IProps>(props => (
  <TabletDataContext.Consumer>
    {tabletData => <WelcomeBoardWrapper tabletData={tabletData} {...props} />}
  </TabletDataContext.Consumer>
));



// WEBPACK FOOTER //
// ./src/screens/Onboarding/WelcomeBoard/WelcomeBoard.tsx